import { FC } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  Typography,
  Link,
  Stack,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation, Trans } from 'react-i18next';
import { CopyButton } from './CopyElements/CopyButton';
import { SpacingWrapper } from './SpacingWrapper';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      margin: theme.spacing(0, 1),
    },
    closeButton: {
      color: theme.palette.primary.main,
    },
    content: {
      margin: theme.spacing(0, 1),
    },
    formattedError: {
      fontFamily: 'monospace',
      fontSize: theme.typography.fontSize,
      whiteSpace: 'pre',
    },
    descriptionBox: {
      overflow: 'auto',
      maxHeight: '450px',
      backgroundColor: theme.palette.grey.A400,
      padding: theme.spacing(2),
      scrollbarColor: `${theme.palette.grey.A200} transparent`,
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.grey.A200,
      },
      '&::-webkit-scrollbar-corner': {
        backgroundColor: theme.palette.grey.A400,
      },
    },
  })
);

/**
 * @summary Диалоговое окно для отображения ошибки, приходящей с бэкэнда.
 *
 * @param url - URL, по которому произошла ошибка.
 * @param description - Описание ошибки (human readable error), приходит с бэкэнда.
 * @param failDescription - Trace с бэкэнда.
 * @param open - Флаг того, что диалоговое окно открыто.
 * @param onClose - Коллбэк при закрытии диалогового окна.
 *
 * @description Диалоговое окно, содержащее информацию о произошедшей ошибке, а также уведомление о том,
 * что нужно передать информацию в техническую поддержку. Содержит в себе кнопку, позволяющую раскрыть
 * подробности ошибки: URL, описание и трейсбек с бэкэнда. В правом верхнем углу описания
 * есть кнопка, позволяющая скопировать содержимое подробностей ошибки в буфер обмена.
 */
const DialogForError: FC<{
  url: string;
  description: string;
  failDescription: string;
  open: boolean;
  onClose: () => void;
}> = ({ url, description, failDescription, open, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <Dialog fullWidth open={open} maxWidth="md" data-testid="dialogForError">
      <Stack spacing={2}>
        <DialogTitle className={classes.header}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="nowrap">
            <Typography variant="subtitle2" color="error">
              {t('An error has occurred')}
            </Typography>
            <IconButton className={classes.closeButton} onClick={onClose} size="large">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.content}>
          <SpacingWrapper>
            <Typography variant="body2">
              <Trans i18nKey="Please, contact technical support and pass on the information indicated below.">
                'Please, contact{' '}
                <Link href="https://help.ideco.ru" target="_blank" rel="noreferrer">
                  technical support
                </Link>{' '}
                and pass on the information indicated below.'
              </Trans>
            </Typography>
          </SpacingWrapper>
          <CopyButton text={`${url}\n${description}\n${failDescription}`} color="secondary" />
          <Box className={classes.descriptionBox}>
            <Typography className={classes.formattedError}>{`URL: ${url}\n\n`}</Typography>
            <Typography className={classes.formattedError}>{description}</Typography>
            <Typography className={classes.formattedError}>{failDescription}</Typography>
          </Box>
        </DialogContent>
      </Stack>
    </Dialog>
  );
};

export { DialogForError };
